// Command / code box with copy (mockup ui.codeBox, element 008). One line → .cmd-box with a "$" prompt; several lines →
// .code-box (scrollable <pre>, language tag). The copy button turns into a check for 1.6 s and a toast confirms.
import { useState } from 'react';
import { useT } from '../i18n';
import { cls, copyText } from '../lib/util';
import { Icon } from './Icon';
import { toast } from './layers';

/** Copy text with the standard toasts (복사했어요 / 복사하지 못했어요). Returns whether it worked. */
export async function copyWithToast(text: string, t: (k: string) => string): Promise<boolean> {
  const ok = await copyText(text);
  toast(ok ? t('common.copied') : t('common.copyFail'), ok ? 'ok' : 'warn');
  return ok;
}

export interface CodeBoxProps {
  text: string;
  /** Accessible name of the copy button (default "복사"). */
  label?: string;
  /** Language tag of a multi-line box ('bash', 'toml' …). */
  lang?: string;
  /** Prompt sign of a one-line box (default '$'). */
  prompt?: string;
  copy?: boolean;
  /** Text actually copied when it differs from what is shown (e.g. without masking marks). */
  copyValue?: string;
  className?: string;
}
export function CodeBox({ text, label, lang, prompt = '$', copy = true, copyValue, className }: CodeBoxProps) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  const multi = /\n/.test(text);
  const name = label ?? t('common.copy');
  const onCopy = async () => {
    if (await copyWithToast(copyValue ?? text, t)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    }
  };
  const btn = copy ? (
    <button className={multi ? 'code-box__copy' : 'cmd-box__copy'} type="button" aria-label={name} data-tip={name} onClick={onCopy}>
      <Icon name={copied ? 'check' : 'copy'} />
    </button>
  ) : null;
  if (multi)
    return (
      <div className={cls('code-box', copied && 'is-copied', className)}>
        {lang ? <span className="code-box__lang">{lang}</span> : null}
        <pre tabIndex={0}>
          <code>{text}</code>
        </pre>
        {btn}
      </div>
    );
  return (
    <div className={cls('cmd-box', copied && 'is-copied', className)}>
      <code tabIndex={0}>
        <em aria-hidden="true">{prompt}</em>
        {text}
      </code>
      {btn}
    </div>
  );
}
